import React, { useEffect, useRef, useState } from 'react';
import { Activity, Zap, Cpu, Sliders, Volume2, ShieldCheck, Sparkles, RefreshCw, Compass } from 'lucide-react';
import { motion } from 'motion/react';
import { LingodbhavaAxisBridge, type CriticalLineCoordinate, type VedicSwaraType } from '../core/lingodbhava_invariant_axis_bridge';

interface SvaraContourVisualizerProps {
  currentPitchHz: number;
  clarity: number;
  svaraAccent?: 'UDATTA' | 'ANUDATTA' | 'SVARITA' | 'SAMANYA';
  centDeviation?: number;
  targetBasePitchHz?: number;
  onBufferConfigChange?: (bufferSize: number) => void;
  onWasmToggle?: (enabled: boolean) => void;
}

export default function SvaraContourVisualizer({
  currentPitchHz,
  clarity,
  svaraAccent = 'SVARITA',
  centDeviation = 0,
  targetBasePitchHz = 220,
  onBufferConfigChange,
  onWasmToggle,
}: SvaraContourVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const historyRef = useRef<{ pitch: number; accent: string; time: number }[]>([]);
  const [bufferSize, setBufferSize] = useState<number>(1024);
  const [wasmEnabled, setWasmEnabled] = useState<boolean>(false);
  const [dtwAccuracy, setDtwAccuracy] = useState<number>(94.5);

  const [livePitch, setLivePitch] = useState<number>(currentPitchHz);
  const [liveAccent, setLiveAccent] = useState<'UDATTA' | 'ANUDATTA' | 'SVARITA' | 'SAMANYA'>(svaraAccent);
  const [liveCents, setLiveCents] = useState<number>(centDeviation);

  // Lingodbhava Invariant Axis Bridge (Riemann Critical Line Operator, Purist Vol. III)
  const bridgeRef = useRef(new LingodbhavaAxisBridge());
  const [criticalCoords, setCriticalCoords] = useState<CriticalLineCoordinate>({
    sigma: 0.5,
    t: 14.134725,
    offAxisCentError: 0.0,
    swaraClassification: 'SAMAHARA',
    fenichelDampingFactor: 1.0,
  });

  // Sync props to internal state
  useEffect(() => {
    setLivePitch(currentPitchHz);
    setLiveAccent(svaraAccent);
    setLiveCents(centDeviation);
  }, [currentPitchHz, svaraAccent, centDeviation]);

  // Listen to global pitch-metric custom events
  useEffect(() => {
    const handlePitchMetric = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const { pitch, accent, centDeviation } = customEvent.detail;
        if (typeof pitch === 'number') setLivePitch(pitch);
        if (accent) setLiveAccent(accent);
        if (typeof centDeviation === 'number') setLiveCents(centDeviation);
      }
    };

    window.addEventListener('pitch-metric', handlePitchMetric);
    return () => window.removeEventListener('pitch-metric', handlePitchMetric);
  }, []);

  // Maintain rolling pitch history
  useEffect(() => {
    const now = performance.now();
    historyRef.current.push({
      pitch: livePitch > 50 ? livePitch : 0,
      accent: liveAccent,
      time: now,
    });

    if (historyRef.current.length > 120) {
      historyRef.current.shift();
    }

    // Dynamic DTW accuracy estimation
    if (livePitch > 50) {
      const matchScore = Math.max(70, Math.min(99.8, 100 - Math.abs(liveCents) * 0.15));
      setDtwAccuracy((prev) => Math.round((prev * 0.9 + matchScore * 0.1) * 10) / 10);
    }

    // Project onto Lingodbhava Critical Line (s = sigma + i*t)
    const swaraType: VedicSwaraType =
      liveAccent === 'UDATTA' ? 'UDATTA' :
      liveAccent === 'ANUDATTA' ? 'ANUDATTA' :
      liveAccent === 'SVARITA' ? 'SVARITA' : 'SAMAHARA';

    const coords = bridgeRef.current.projectVocalPitchToCriticalLine(
      livePitch,
      targetBasePitchHz,
      swaraType,
      0.02
    );
    setCriticalCoords(coords);
  }, [livePitch, liveAccent, liveCents, targetBasePitchHz]);

  // Render Real-Time Canvas with Accent Bands
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;

    const render = () => {
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      // Background grid
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);

      const minPitch = 80;
      const maxPitch = 380;
      const pitchToY = (hz: number) => {
        if (hz <= 0) return height - 10;
        const norm = (hz - minPitch) / (maxPitch - minPitch);
        return height - Math.max(10, Math.min(height - 10, norm * height));
      };

      // Band 1: Udatta (High Pitch Accent) - Top region
      const udattaY = pitchToY(260);
      ctx.fillStyle = 'rgba(245, 158, 11, 0.08)';
      ctx.fillRect(0, 0, width, udattaY);
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.3)';
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, udattaY);
      ctx.lineTo(width, udattaY);
      ctx.stroke();

      // Band 2: Svarita (Middle Circumflex Accent)
      const svaritaY = pitchToY(190);
      ctx.fillStyle = 'rgba(16, 185, 129, 0.08)';
      ctx.fillRect(0, udattaY, width, svaritaY - udattaY);
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.3)';
      ctx.beginPath();
      ctx.moveTo(0, svaritaY);
      ctx.lineTo(width, svaritaY);
      ctx.stroke();

      // Band 3: Anudatta (Low Pitch Accent) - Bottom region
      ctx.fillStyle = 'rgba(59, 130, 246, 0.08)';
      ctx.fillRect(0, svaritaY, width, height - svaritaY);

      ctx.setLineDash([]); // Reset line dash

      // Target Reference Baseline
      const refY = pitchToY(targetBasePitchHz);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, refY);
      ctx.lineTo(width, refY);
      ctx.stroke();

      // Render Trajectory Curve
      const history = historyRef.current;
      if (history.length > 1) {
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        for (let i = 1; i < history.length; i++) {
          const ptPrev = history[i - 1];
          const ptCurr = history[i];

          const xPrev = ((i - 1) / 120) * width;
          const xCurr = (i / 120) * width;
          const yPrev = pitchToY(ptPrev.pitch);
          const yCurr = pitchToY(ptCurr.pitch);

          let strokeColor = '#94a3b8';
          if (ptCurr.accent === 'UDATTA') strokeColor = '#f59e0b';
          else if (ptCurr.accent === 'ANUDATTA') strokeColor = '#3b82f6';
          else if (ptCurr.accent === 'SVARITA') strokeColor = '#10b981';

          ctx.strokeStyle = strokeColor;
          ctx.beginPath();
          ctx.moveTo(xPrev, yPrev);
          ctx.lineTo(xCurr, yCurr);
          ctx.stroke();
        }
      }

      animationId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animationId);
  }, [targetBasePitchHz]);

  const handleBufferSelect = (size: number) => {
    setBufferSize(size);
    if (onBufferConfigChange) onBufferConfigChange(size);
  };

  const handleWasmToggle = () => {
    const next = !wasmEnabled;
    setWasmEnabled(next);
    if (onWasmToggle) onWasmToggle(next);
  };

  const getAccentBadgeColor = () => {
    switch (liveAccent) {
      case 'UDATTA':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      case 'ANUDATTA':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
      case 'SVARITA':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      default:
        return 'bg-slate-700/40 text-slate-300 border-slate-600';
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-3">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20 text-amber-400">
            <Activity className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2">
              Vedic Phonetics Svara Visualizer
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                Live DSP
              </span>
            </h3>
            <p className="text-xs text-slate-400">Real-time Udatta, Anudatta & Svarita accent trajectory matching</p>
          </div>
        </div>

        {/* Accent Metric Badges */}
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 text-xs font-bold rounded-lg border ${getAccentBadgeColor()}`}>
            {liveAccent} ACCENT
          </div>
          <div className="px-3 py-1 text-xs font-mono bg-slate-800 text-slate-200 rounded-lg border border-slate-700">
            {livePitch > 0 ? `${livePitch.toFixed(1)} Hz` : 'MUTE'}
          </div>
        </div>
      </div>

      {/* Canvas Viewport */}
      <div className="relative rounded-xl overflow-hidden border border-slate-800 shadow-inner">
        <canvas ref={canvasRef} width={640} height={200} className="w-full h-48 block" />

        {/* Band Labels Overlay */}
        <div className="absolute top-2 left-3 flex flex-col space-y-1 text-[10px] font-semibold tracking-wider pointer-events-none">
          <span className="text-amber-400 bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-500/30 w-fit">
            UDATTA (High &gt; 260Hz)
          </span>
          <span className="text-emerald-400 bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-500/30 w-fit">
            SVARITA (190-260Hz)
          </span>
          <span className="text-blue-400 bg-blue-950/60 px-1.5 py-0.5 rounded border border-blue-500/30 w-fit">
            ANUDATTA (Low &lt; 190Hz)
          </span>
        </div>

        {/* Live Cent Deviation Overlay */}
        <div className="absolute top-2 right-3 bg-slate-950/80 backdrop-blur-md px-3 py-1.5 rounded-xl border border-slate-800 text-right">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Cent Deviation</div>
          <div
            className={`text-sm font-mono font-bold ${
              Math.abs(liveCents) < 25
                ? 'text-emerald-400'
                : Math.abs(liveCents) < 50
                ? 'text-amber-400'
                : 'text-rose-400'
            }`}
          >
            {liveCents > 0 ? `+${liveCents}` : liveCents} cents
          </div>
        </div>

        {/* DTW Match Badge */}
        <div className="absolute bottom-2 right-3 bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-lg border border-slate-800 flex items-center space-x-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs text-slate-300 font-medium">DTW Match:</span>
          <span className="text-xs font-bold text-amber-400">{dtwAccuracy}%</span>
        </div>
      </div>

      {/* Control Strip for Micro-Buffer & WASM Optimization */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
        {/* Micro-Buffer Size Selector for Budget Devices */}
        <div className="bg-slate-800/60 p-2.5 rounded-xl border border-slate-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sliders className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-300 font-medium">Micro-Buffer (Latency):</span>
          </div>
          <div className="flex space-x-1">
            {[512, 1024, 2048].map((size) => (
              <button
                key={size}
                onClick={() => handleBufferSelect(size)}
                className={`px-2 py-0.5 text-xs font-mono rounded transition-colors ${
                  bufferSize === size
                    ? 'bg-amber-500 text-slate-950 font-bold'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* WASM Bitwise Engine Acceleration Toggle */}
        <div className="bg-slate-800/60 p-2.5 rounded-xl border border-slate-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Cpu className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-slate-300 font-medium">WASM Pitch Mode:</span>
          </div>
          <button
            onClick={handleWasmToggle}
            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors flex items-center space-x-1.5 ${
              wasmEnabled
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                : 'bg-slate-700/60 text-slate-400 border border-slate-600'
            }`}
          >
            <Zap className={`w-3 h-3 ${wasmEnabled ? 'fill-emerald-400 text-emerald-400' : ''}`} />
            <span>{wasmEnabled ? 'Bitwise WASM' : 'Standard YIN'}</span>
          </button>
        </div>
      </div>

      {/* Lingodbhava Invariant Axis Strip (Riemann Critical Line s = sigma + i*t, Purist Vol. III) */}
      <div className="bg-slate-950/70 p-3 rounded-xl border border-amber-500/20 flex flex-wrap items-center justify-between gap-2 text-xs font-mono">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-amber-400" />
          <span className="text-amber-200/90 font-semibold">Lingodbhava Invariant Axis:</span>
          <span className="text-stone-300">
            s = <span className="text-amber-300 font-bold">{criticalCoords.sigma.toFixed(5)}</span> + i·
            <span className="text-cyan-300 font-bold">{criticalCoords.t.toFixed(4)}</span>
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-stone-400">
            Fenichel Contraction: <span className="text-emerald-400 font-bold">{(criticalCoords.fenichelDampingFactor * 100).toFixed(1)}%</span>
          </span>
          <span className="text-stone-400">
            Solinas: <code className="text-amber-300 font-mono text-[10px]">0x5a17a942b6c8d31e</code>
          </span>
        </div>
      </div>
    </div>
  );
}
